import React from 'react';
import type {
  InvestmentInfoRow,
  InvestmentInfoGroupChild,
} from '@app-types/investmentIndicators';
import InvestmentInfoValue from './InvestmentInfoValue';
import './InvestmentInfoTable.less';

export interface InvestmentInfoTableProps {
  rows: InvestmentInfoRow[];
  /** Optional Figma table node id (echoed as data-node-id on <table>). */
  nodeId?: string;
  /** Optional visually-hidden caption for screen readers. */
  caption?: string;
  /** Scoped responsive column layout for blocks with documented requirements. */
  layoutVariant?: InvestmentInfoTableLayoutVariant;
}

/**
 * Scoped column-width profiles.
 *
 * - `basic`: Basic-aligned 42/18/40 desktop profile (`60/40` simple divider).
 * - `period-labels`: 40/30/30 desktop profile (`70/30` simple divider).
 * - `period-labels-wide-value`: work_items_101 — keeps the work_items_110 30%
 *   period sub-label but widens the value column to 40%, so the simple-row
 *   divider lands on Basic's `60/40`.
 * - `technical-signals`: 40/34/26 desktop and mobile profile.
 */
export type InvestmentInfoTableLayoutVariant =
  | 'basic'
  | 'period-labels'
  | 'period-labels-wide-value'
  | 'technical-signals';

const toneClass = (tone?: string): string => (tone ? ` is-${tone}` : '');

/**
 * Reusable information table for the Investment Indicators screen.
 *
 * Two shapes in the same table:
 * - Simple row: `<th colspan="2">label</th><td>value</td>` — value spans full width
 * - Group row: first child has `<th rowspan={N}>groupLabel</th>` plus own
 *   sub-label + value; subsequent children only render sub-label + value.
 *
 * Visual grid pattern (1px `#999` gutters) mirrors dividend-kv-table.
 */
const InvestmentInfoTable: React.FC<InvestmentInfoTableProps> = ({
  rows,
  nodeId,
  caption,
  layoutVariant,
}) => (
  <table
    className={`investment-info-table${layoutVariant ? ` investment-info-table--${layoutVariant}` : ''}`}
    data-node-id={nodeId}
  >
    {caption && <caption className="investment-info-table__caption">{caption}</caption>}
    {/*
      Fixed three-column model shared by both row shapes. Simple rows use
      th[colSpan=2] so the grey label spans label + sub-label; group rows fill
      all three columns. Column widths live in InvestmentInfoTable.less so the
      grey headers cannot steal width from the white value column.
    */}
    <colgroup>
      <col className="investment-info-table__col investment-info-table__col--label" />
      <col className="investment-info-table__col investment-info-table__col--sub-label" />
      <col className="investment-info-table__col investment-info-table__col--value" />
    </colgroup>
    <tbody>
      {rows.flatMap((row) => {
        if (!row.children || row.children.length === 0) {
          // Simple 2-cell row (label spans first two columns)
          return [
            <tr key={row.key} data-node-id={row.nodeIdWeb}>
              <th
                scope="row"
                colSpan={2}
                className="investment-info-table__cell investment-info-table__cell--header"
              >
                {row.label}
              </th>
              <td
                className={`investment-info-table__cell investment-info-table__cell--value${toneClass(row.tone)}`}
              >
                <InvestmentInfoValue value={row.value} />
              </td>
            </tr>,
          ];
        }
        // Group row: first child gets rowspan on group label; others just render sub + value
        const groupSize = row.children.length;
        return row.children.map((child: InvestmentInfoGroupChild, index) => (
          <tr key={`${row.key}-${child.key}`} data-node-id={child.nodeIdWeb}>
            {index === 0 && (
              <th
                scope="rowgroup"
                rowSpan={groupSize}
                className={[
                  'investment-info-table__cell',
                  'investment-info-table__cell--header',
                  'investment-info-table__cell--group-label',
                  row.groupLabelVerticalAlign === 'top'
                    ? 'investment-info-table__cell--group-label-top'
                    : '',
                ].filter(Boolean).join(' ')}
                data-node-id={row.nodeIdWeb}
              >
                {row.label}
              </th>
            )}
            <th
              scope="row"
              className="investment-info-table__cell investment-info-table__cell--sub-label"
            >
              {child.label}
            </th>
            <td
              className={`investment-info-table__cell investment-info-table__cell--value${toneClass(child.tone)}`}
            >
              <InvestmentInfoValue value={child.value} />
            </td>
          </tr>
        ));
      })}
    </tbody>
  </table>
);

export default InvestmentInfoTable;
